import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  formatNumber,


  type HomeSummary,
  type Workout,
  type Gym,
  type Meal,
} from '@jungles/shared';
import { useApi } from '../lib/hooks';
import { useHomeStartup } from '../lib/startup';
import { useUser } from '../app/session';
import { TelegramAvatar } from '../components/TelegramAvatar';
import {
  ErrorState,
  Header,
  JungleStrip,
  Loader,
  Screen,
  TabBar,
} from '../components/Chrome';
import { ChevronIcon, SparkIcon } from '../components/icons';
import { WorkoutPicker } from '../components/WorkoutPicker';
import { FoodPicker } from '../components/FoodPicker';
import { GymLogo } from '../components/GymLogo';
import { HomeQuickButtons } from '../components/HomeQuickButtons';
import type { HomeWorkouts } from '../lib/home-quick';
import { HomeStatistics } from '../components/HomeStatistics';
import '../styles/home-dashboard.css';

/** H1–H3 · Главная (SPEC §5). На главной нет списков лиг — только плашка. */

export function HomeScreen() {
  const user = useUser();
  return <HomeContent key={user.id} />;
}

function HomeContent() {
  const user = useUser();
  const navigate = useNavigate();
  const { data: latestHome, error, loading, reload } = useApi<HomeSummary>('/api/home');
  const [heldHome, setHeldHome] = useState<{ data: HomeSummary; draft: Workout | null } | null>(null);
  const data = heldHome?.data ?? latestHome;
  // Reuse the server's owner-scoped, latest-updated saved draft selection.
  const { data: workouts, error: draftError, loading: draftLoading, reload: reloadDraft } =
    useApi<HomeWorkouts>('/api/workouts/summary');
  const resolvedDraft = !draftError && workouts?.draft?.status === 'draft' ? workouts.draft : null;
  const [settledDraft, setSettledDraft] = useState<Workout | null>(null);

  // Background revalidation must not collapse a target already on screen.
  const currentDraft = draftLoading ? settledDraft : resolvedDraft;
  useEffect(() => { if (!draftLoading) setSettledDraft(resolvedDraft); }, [draftLoading, resolvedDraft]);
  useEffect(() => {
    const refresh = () => { if (document.visibilityState === 'visible') { reloadDraft(); reload(); } };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [reloadDraft, reload]);
  // homeGymName includes the city for other screens; use the canonical name here.
  const { data: homeGym, loading: gymLoading, error: gymError } = useApi<Gym>(user.homeGymId ? `/api/gyms/${encodeURIComponent(user.homeGymId)}` : null);
  useHomeStartup(loading || draftLoading || gymLoading, !!(error || draftError || gymError), {
    home: !loading && !!data, summary: !draftLoading && !!workouts, gym: !gymLoading,
  });
  const gymName = homeGym?.id === user.homeGymId ? homeGym.name : user.homeGymId ? 'Мой зал' : 'Без зала';
  const [picker, setPicker] = useState<'workout' | 'food' | null>(null);
  const [foodContext, setFoodContext] = useState<{ meal: Meal; date: string } | null>(null);

  if (loading && !data) {
    return (
      <Screen tabs className="home-neutral">
        <Header />
        <Loader />
        <TabBar />
      </Screen>
    );
  }

  if (!data) {
    return (
      <Screen tabs className="home-neutral">
        <Header />
        <ErrorState message={error ?? 'Нет данных'} onRetry={reload} />
        <TabBar />
      </Screen>
    );
  }

  const { week, points, league, tip } = data;


  return (
    <Screen tabs className="home-neutral">
      <Header />

      {tip ? (
        <section aria-label="Подсказка jungles AI" style={{ margin: '4px 0 8px' }}>
          <div className="ai-ctx">
            <SparkIcon />
            {tip.context}
            <ChevronIcon size={12} />
          </div>
          <p className="ai-h">
            <span className="emo" aria-hidden="true">
              {tip.emoji}
            </span>
            {tip.title}
          </p>
        </section>
      ) : null}

      <div className="me-flat home-identity">
        <TelegramAvatar key={user.id} photoUrl={user.photoUrl} name={user.name} className="me-av" size={52} />
        <div className="nm">
          <b>{user.name.split(' ')[0]}</b>
          <span className="gym-inline">
            {user.homeGymName ? <GymLogo name={user.homeGymName} logoUrl={user.homeGymLogoUrl} size="small" /> : null}
            <span title={gymName}>{gymName}</span>
          </span>
        </div>
        <button
          className="me-pts"
          type="button"
          onClick={() => navigate('/arena')}
          style={{ background: 'none', border: 0, cursor: 'pointer' }}
        >
          <span className="me-n">
            {formatNumber(points.total)}
            <img className="pt-ic" src="/assets/ui/points-diamond-bicep.webp" alt="очков" />
          </span>
          {league ? (
            <span className="me-lg">
              <img src={league.icon} alt="" />
              {league.name} {league.rank ? <b>#{league.rank}</b> : null}
            </span>
          ) : null}
        </button>
      </div>

      <HomeQuickButtons user={user} home={data} summary={workouts} draft={currentDraft}
        summaryLoading={draftLoading} summaryError={!!draftError}
        onHold={held => setHeldHome(held ? { data, draft: currentDraft } : null)}
        onNewDay={() => { reload(); reloadDraft(); }}
        onWorkout={selected => selected ? navigate(`/workouts/${encodeURIComponent(selected.id)}`) : setPicker('workout')}
        onFood={(meal, date) => { setFoodContext({ meal, date }); setPicker('food'); }} />

      <section className="hero" aria-label="Моя неделя">
        <div className="row">
          <h2>Моя неделя</h2>
          <span className="nt">{week.rangeLabel}</span>
        </div>

        <div className="wk">
          {week.days.map((day) => (
            <div
              key={day.date}
              className={`day${day.isToday ? ' today' : ''}${day.isFuture ? ' fut' : ''}`}
            >
              {day.label}
              <b>{day.dayOfMonth}</b>
              <span className="dots">
                <i className={day.hasWorkout ? 'dt t' : 'dt'} />
                <i className={day.hasFood ? 'dt food' : 'dt'} />
              </span>
            </div>
          ))}
        </div>

        <div className="goal">
          <div className="row">
            <Link className="goal-link" to="/workouts"><b>Тренировки</b><ChevronIcon size={14} /></Link>
            <span>
              <b>{week.workoutsDone}</b> из {week.workoutsGoal} в неделю
            </span>
          </div>
          <div
            className="saus t"
            role="img"
            aria-label={`${week.workoutsDone} из ${week.workoutsGoal} тренировок`}
          >
            {Array.from({ length: week.workoutsGoal }, (_, index) => (
              <span key={index} className={index < week.workoutsDone ? 'on' : undefined} />
            ))}
          </div>
        </div>

        <div className="goal">
          <div className="row">
            <Link className="goal-link" to="/nutrition"><b>Питание</b><ChevronIcon size={14} /></Link>
            <span>
              <b>{week.foodDays}</b> из {week.foodDaysGoal} дней
            </span>
          </div>
          <div
            className="saus food"
            role="img"
            aria-label={`${week.foodDays} из ${week.foodDaysGoal} дней с записью питания`}
          >
            {week.days.map((day) => (
              <span key={day.date} className={day.hasFood ? 'on' : undefined} />
            ))}
          </div>
        </div>

        <p className="nt" style={{ margin: '14px 0 0' }}>
          <button
            className="back"
            type="button"
            onClick={() => navigate('/profile')}
            style={{ minHeight: 'auto', padding: 0, textDecoration: 'underline' }}
          >
            Изменить цели
          </button>
        </p>
      </section>

      <HomeStatistics user={user} />

      <JungleStrip />
      <TabBar />

      {picker === 'workout' ? <WorkoutPicker onClose={() => setPicker(null)} /> : null}
      {picker === 'food' && foodContext ? (
        <FoodPicker meal={foodContext.meal} date={foodContext.date} onClose={() => setPicker(null)} />
      ) : null}
    </Screen>
  );
}
